import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AccountLayout from '../components/account/AccountLayout';
import { useAccountCounts } from '../components/account/useAccountCounts';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import JoinedCompetitionCard from '../components/common/JoinedCompetitionCard';
import EvaluationCard from '../components/common/EvaluationCard';
import HostedCompetitionCard from '../components/common/HostedCompetitionCard';
import ListControls from '../components/common/ListControls';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import { CompetitionSummary, EvaluationSummary, HostedCompetitionSummary } from '../types/interfaces';
import './Profile.css';

type WorkTab = 'joined' | 'evaluations' | 'hosted';

// Shown names for each tab; the URL values above stay the same for old links.
const TAB_LABEL: Record<WorkTab, string> = { joined: 'joined', evaluations: 'judging', hosted: 'hosting' };

type FilterableRecord = {
  title?: string;
  status?: string;
  type?: string;
};

const TAB_ORDER: WorkTab[] = ['joined', 'evaluations', 'hosted'];

const STATUS_LABEL_OVERRIDES: Record<string, string> = {
  all: 'All statuses',
};

const toTitleCase = (value: string): string =>
  value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const ProfileWork: React.FC = () => {
  const { data } = useAccountCounts();
  const [searchParams, setSearchParams] = useSearchParams();

  const [joinedCompetitions, setJoinedCompetitions] = useState<CompetitionSummary[]>([]);
  const [evaluations, setEvaluations] = useState<EvaluationSummary[]>([]);
  const [hostedCompetitions, setHostedCompetitions] = useState<HostedCompetitionSummary[]>([]);

  const [loading, setLoading] = useState<Record<WorkTab, boolean>>({
    joined: true,
    evaluations: true,
    hosted: true,
  });
  const [errors, setErrors] = useState<Record<WorkTab, string>>({
    joined: '',
    evaluations: '',
    hosted: '',
  });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const filtersActive = search.trim() !== '' || statusFilter !== 'all' || typeFilter !== 'all';
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setTypeFilter('all');
  };
  const clearFiltersButton = filtersActive ? (
    <Button variant="ghost" size="sm" onClick={clearFilters}>
      Clear filters
    </Button>
  ) : null;

  const activeTab = useMemo<WorkTab>(() => {
    const tabParam = (searchParams.get('tab') || '').toLowerCase();
    return TAB_ORDER.includes(tabParam as WorkTab) ? (tabParam as WorkTab) : 'joined';
  }, [searchParams]);

  const activeRecords = useMemo<FilterableRecord[]>(() => {
    if (activeTab === 'joined') return joinedCompetitions;
    if (activeTab === 'evaluations') return evaluations;
    return hostedCompetitions;
  }, [activeTab, joinedCompetitions, evaluations, hostedCompetitions]);

  const statusOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        activeRecords
          .map((item) => (item.status || '').toLowerCase().trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));

    return [
      { label: STATUS_LABEL_OVERRIDES.all, value: 'all' },
      ...values.map((value) => ({
        label: STATUS_LABEL_OVERRIDES[value] || toTitleCase(value),
        value,
      })),
    ];
  }, [activeRecords]);

  const typeOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        activeRecords
          .map((item) => (item.type || 'unknown').toLowerCase().trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));

    return [
      { label: 'All types', value: 'all' },
      ...values.map((value) => ({
        label: value === 'unknown' ? 'Unknown' : value.toUpperCase(),
        value,
      })),
    ];
  }, [activeRecords]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading({ joined: true, evaluations: true, hosted: true });
      setErrors({ joined: '', evaluations: '', hosted: '' });

      const token = authUtils.getStoredToken();
      const [joinedRes, evaluationsRes, hostedRes] = await Promise.allSettled([
        apiService.post('/competitions/list_summary', {}, { headers: { Authorization: `Bearer ${token}` } }),
        apiService.post('/evaluations/list_evaluations', {}, { headers: { Authorization: `Bearer ${token}` } }),
        apiService.get('/hosts/my_competitions', { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (joinedRes.status === 'fulfilled') {
        setJoinedCompetitions(joinedRes.value.data || []);
      } else {
        console.error('Error loading joined competitions:', joinedRes.reason);
        setErrors((prev) => ({ ...prev, joined: 'Failed to load joined competitions' }));
      }

      if (evaluationsRes.status === 'fulfilled') {
        setEvaluations(evaluationsRes.value.data || []);
      } else {
        console.error('Error loading evaluations:', evaluationsRes.reason);
        setErrors((prev) => ({ ...prev, evaluations: 'Failed to load evaluations' }));
      }

      if (hostedRes.status === 'fulfilled') {
        setHostedCompetitions(hostedRes.value.data || []);
      } else {
        console.error('Error loading hosted competitions:', hostedRes.reason);
        setErrors((prev) => ({ ...prev, hosted: 'Failed to load hosted competitions' }));
      }

      setLoading({ joined: false, evaluations: false, hosted: false });
    };

    loadAll();
  }, []);

  useEffect(() => {
    setSearch('');
    setStatusFilter('all');
    setTypeFilter('all');
  }, [activeTab]);

  const filteredJoinedCompetitions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return joinedCompetitions.filter((competition) => {
      const matchesQuery = !q || (competition.title || '').toLowerCase().includes(q);
      const status = (competition.status || '').toLowerCase();
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const type = (competition.type || 'unknown').toLowerCase();
      const matchesType = typeFilter === 'all' || type === typeFilter;
      return matchesQuery && matchesStatus && matchesType;
    });
  }, [joinedCompetitions, search, statusFilter, typeFilter]);

  const filteredEvaluations = useMemo(() => {
    const q = search.trim().toLowerCase();
    return evaluations.filter((evaluation) => {
      const matchesQuery = !q || (evaluation.title || '').toLowerCase().includes(q);
      const status = (evaluation.status || '').toLowerCase();
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const type = (evaluation.type || 'unknown').toLowerCase();
      const matchesType = typeFilter === 'all' || type === typeFilter;
      return matchesQuery && matchesStatus && matchesType;
    });
  }, [evaluations, search, statusFilter, typeFilter]);

  const filteredHostedCompetitions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hostedCompetitions.filter((competition) => {
      const matchesQuery = !q || (competition.title || '').toLowerCase().includes(q);
      const status = (competition.status || '').toLowerCase();
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const type = (competition.type || 'unknown').toLowerCase();
      const matchesType = typeFilter === 'all' || type === typeFilter;
      return matchesQuery && matchesStatus && matchesType;
    });
  }, [hostedCompetitions, search, statusFilter, typeFilter]);

  const tabStats = useMemo(
    () => ({
      joined: joinedCompetitions.length,
      evaluations: evaluations.length,
      hosted: hostedCompetitions.length,
    }),
    [joinedCompetitions.length, evaluations.length, hostedCompetitions.length]
  );

  const updateTab = (tab: WorkTab) => {
    setSearchParams({ tab });
  };

  const renderActiveTabContent = () => {
    const activeLoading = loading[activeTab];
    const activeError = errors[activeTab];

    if (activeLoading) {
      return (
        <div className="profile-joined__loader">
          <LoadingSpinner size="md" text={`Loading ${TAB_LABEL[activeTab]}...`} />
        </div>
      );
    }

    if (activeError) {
      return <div className="profile-joined__error">{activeError}</div>;
    }

    if (activeTab === 'joined') {
      if (filteredJoinedCompetitions.length === 0) {
        return (
          <div className="profile-joined__empty">
            {joinedCompetitions.length === 0 ? (
              <p>You have not joined any competitions yet.</p>
            ) : (
              <>
                <p>No joined competitions match these filters.</p>
                {clearFiltersButton}
              </>
            )}
          </div>
        );
      }
      return (
        <div className="profile-joined__list">
          {filteredJoinedCompetitions.map((competition) => (
            <JoinedCompetitionCard key={competition.competition_id} competition={competition} />
          ))}
        </div>
      );
    }

    if (activeTab === 'evaluations') {
      if (filteredEvaluations.length === 0) {
        return (
          <div className="profile-joined__empty">
            {evaluations.length === 0 ? (
              <p>No judging assignments yet.</p>
            ) : (
              <>
                <p>No evaluations match these filters.</p>
                {clearFiltersButton}
              </>
            )}
          </div>
        );
      }
      return (
        <div className="profile-evaluations__list">
          {filteredEvaluations.map((evaluation) => (
            <EvaluationCard key={evaluation.competition_id} evaluation={evaluation} />
          ))}
        </div>
      );
    }

    if (filteredHostedCompetitions.length === 0) {
      return (
        <div className="profile-joined__empty">
          {hostedCompetitions.length === 0 ? (
            <p>You are not listed as a host for any competitions.</p>
          ) : (
            <>
              <p>No hosted competitions match these filters.</p>
              {clearFiltersButton}
            </>
          )}
        </div>
      );
    }

    return (
      <div className="profile-joined__list">
        {filteredHostedCompetitions.map((competition) => (
          <HostedCompetitionCard key={competition.id} competition={competition} />
        ))}
      </div>
    );
  };

  return (
    <AccountLayout title="My competitions" data={data}>
      <section className="profile-work">
        <div className="profile-work-tabs" role="tablist" aria-label="My competitions" data-tour="work-tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'joined'}
            className={`profile-work-tab ${activeTab === 'joined' ? 'profile-work-tab--active' : ''}`}
            onClick={() => updateTab('joined')}
          >
            Joined
            <span className="profile-nav-badge">{tabStats.joined}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'evaluations'}
            className={`profile-work-tab ${activeTab === 'evaluations' ? 'profile-work-tab--active' : ''}`}
            onClick={() => updateTab('evaluations')}
          >
            Judging
            <span className="profile-nav-badge">{tabStats.evaluations}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'hosted'}
            className={`profile-work-tab ${activeTab === 'hosted' ? 'profile-work-tab--active' : ''}`}
            onClick={() => updateTab('hosted')}
          >
            Hosting
            <span className="profile-nav-badge">{tabStats.hosted}</span>
          </button>
        </div>

        <ListControls
          sticky
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={`Search ${TAB_LABEL[activeTab]}...`}
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={statusOptions}
          typeValue={typeFilter}
          onTypeChange={setTypeFilter}
          typeOptions={typeOptions}
        />

        <div data-tour="work-list">{renderActiveTabContent()}</div>
      </section>
    </AccountLayout>
  );
};

export default ProfileWork;
